import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

/**
 * Admin CRUD for the three service-taxonomy master lists:
 *   - certifications (ISO, NABL, MSME, etc.)
 *   - industries (Automotive, Pharma, etc.)
 *   - applications (Packaging, HVAC, etc.)
 *
 * Powers the multi-select chip pickers on the Post-Listing (Service) form
 * and on the /services browse filters. Values live in three collections
 * that share the identical schema, so this UI is a single component
 * driven by the `kind` tab.
 */
const KINDS = [
  { key: 'certifications', label: 'Certifications', hint: 'ISO 9001, NABL, MSME, PESO, Factory Licence, etc.' },
  { key: 'industries', label: 'Industries Served', hint: 'Which industries the provider works with (Automotive, Pharma, F&B…).' },
  { key: 'applications', label: 'Application Expertise', hint: 'Specific problem areas (Packaging, HVAC, Water Treatment…).' },
];

const AdminServiceTaxonomyTab = () => {
  const [activeKind, setActiveKind] = useState('certifications');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState(null); // {id?, name, active, display_order}
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/service-taxonomy/${activeKind}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load list');
    } finally {
      setLoading(false);
    }
  }, [activeKind]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditor({ name: '', active: true, display_order: items.length });
  const startEdit = (item) => setEditor({ ...item });
  const closeEditor = () => setEditor(null);

  const save = async () => {
    if (!editor?.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editor.id) {
        await api.patch(`/admin/service-taxonomy/${activeKind}/${editor.id}`, {
          name: editor.name.trim(),
          active: editor.active,
          display_order: Number(editor.display_order) || 0,
        });
        toast.success('Updated');
      } else {
        await api.post(`/admin/service-taxonomy/${activeKind}`, {
          name: editor.name.trim(),
          active: editor.active,
          display_order: Number(editor.display_order) || 0,
        });
        toast.success('Added');
      }
      closeEditor();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.patch(`/admin/service-taxonomy/${activeKind}/${item.id}`, { active: !item.active });
      toast.success(item.active ? 'Deactivated' : 'Activated');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/service-taxonomy/${activeKind}/${deleteTarget.id}`);
      toast.success('Deleted');
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const activeMeta = KINDS.find(k => k.key === activeKind);

  return (
    <Card className='p-4 space-y-3' data-testid='admin-service-taxonomy'>
      <div>
        <div className='font-semibold'>Service taxonomy — master lists</div>
        <p className='text-xs text-muted-foreground mt-0.5'>
          These lists power the multi-select pickers on the Service posting form and on /services browse filters. Deactivate an item to hide it from the pickers without deleting historic listings that reference it.
        </p>
      </div>

      <Tabs value={activeKind} onValueChange={setActiveKind}>
        <TabsList data-testid='admin-taxonomy-tabs'>
          {KINDS.map(k => (
            <TabsTrigger key={k.key} value={k.key} data-testid={`admin-taxonomy-tab-${k.key}`}>
              {k.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {KINDS.map(k => (
          <TabsContent key={k.key} value={k.key} className='space-y-3'>
            <div className='flex items-center justify-between gap-3'>
              <div className='text-xs text-muted-foreground'>{k.hint}</div>
              <Button
                size='sm'
                onClick={startNew}
                data-testid={`admin-taxonomy-new-${k.key}`}
              >
                <Plus className='h-4 w-4 mr-1' /> Add {k.label.replace(/s$/, '')}
              </Button>
            </div>

            {loading ? (
              <div className='py-6 text-center text-sm text-muted-foreground'>Loading…</div>
            ) : items.length === 0 ? (
              <div className='py-8 text-center text-sm text-muted-foreground border border-dashed rounded'>
                No entries yet. Click <strong>Add</strong> above.
              </div>
            ) : (
              <div className='rounded-md border divide-y'>
                {items.map(item => (
                  <div
                    key={item.id}
                    className='flex items-center gap-3 px-3 py-2 text-sm'
                    data-testid={`admin-taxonomy-row-${item.id}`}
                  >
                    <span className='w-8 text-xs text-muted-foreground'>#{item.display_order}</span>
                    <span className={`flex-1 ${item.active ? '' : 'text-muted-foreground line-through'}`}>{item.name}</span>
                    <button
                      type='button'
                      onClick={() => toggleActive(item)}
                      className='text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-foreground'
                      data-testid={`admin-taxonomy-toggle-${item.id}`}
                      title={item.active ? 'Deactivate' : 'Activate'}
                    >
                      {item.active ? <ToggleRight className='h-4 w-4 text-green-600' /> : <ToggleLeft className='h-4 w-4' />}
                    </button>
                    <button
                      type='button'
                      onClick={() => startEdit(item)}
                      className='text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-foreground'
                      data-testid={`admin-taxonomy-edit-${item.id}`}
                    >
                      <Pencil className='h-3.5 w-3.5' />
                    </button>
                    <button
                      type='button'
                      onClick={() => setDeleteTarget(item)}
                      className='text-xs inline-flex items-center gap-1 text-red-700 hover:text-red-900'
                      data-testid={`admin-taxonomy-delete-${item.id}`}
                    >
                      <Trash2 className='h-3.5 w-3.5' />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Add / Edit dialog */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && closeEditor()}>
        <DialogContent className='max-w-md' onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{editor?.id ? 'Edit' : 'Add'} {activeMeta?.label.replace(/s$/, '')}</DialogTitle>
            <DialogDescription>{activeMeta?.hint}</DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div>
              <Label className='text-xs'>Name <span className='text-red-600'>*</span></Label>
              <Input
                value={editor?.name || ''}
                onChange={(e) => setEditor(s => ({ ...s, name: e.target.value }))}
                placeholder='e.g. ISO 9001'
                data-testid='admin-taxonomy-editor-name'
                autoFocus
              />
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <Label className='text-xs'>Display order</Label>
                <Input
                  type='number'
                  value={editor?.display_order ?? 0}
                  onChange={(e) => setEditor(s => ({ ...s, display_order: e.target.value }))}
                  data-testid='admin-taxonomy-editor-order'
                />
              </div>
              <div>
                <Label className='text-xs'>Status</Label>
                <div className='mt-1'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => setEditor(s => ({ ...s, active: !s.active }))}
                    data-testid='admin-taxonomy-editor-active'
                  >
                    {editor?.active ? <><ToggleRight className='h-4 w-4 mr-1 text-green-600' /> Active</> : <><ToggleLeft className='h-4 w-4 mr-1' /> Inactive</>}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={closeEditor} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid='admin-taxonomy-editor-save'>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className='max-w-sm'>
          <DialogHeader>
            <DialogTitle>Delete &quot;{deleteTarget?.name}&quot;?</DialogTitle>
            <DialogDescription>
              This removes it from the picker. Listings that already reference
              this value will keep the string — nothing is silently rewritten.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant='destructive' onClick={confirmDelete} data-testid='admin-taxonomy-delete-confirm'>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminServiceTaxonomyTab;
